import { useNavigate } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import type { ReactNode } from 'react';
import { useEffect } from 'react';

import { isAdmin } from '@/lib/permissions';
import { userAtom } from '@/stores/auth.store';

interface AdminGuardProps {
  children: ReactNode;
}

/**
 * Renders its children only for an administrator, and sends anyone else home.
 *
 * The route's `beforeLoad` already rejects a known non-administrator. This
 * covers the case it cannot: AuthProvider resolves the profile *after* the
 * route does, so at `beforeLoad` time the user is often still null. Redirecting
 * on a null user there would bounce every administrator on a cold load.
 *
 * Navigation is imperative rather than `throw redirect(...)`. `redirect()`
 * returns a Response, and TanStack Router only unwraps that from
 * `beforeLoad`/`loader` — thrown from a render it reaches the error boundary
 * and the caller sees "Something went wrong!" instead of being sent home.
 *
 * This is a UX affordance, never a security boundary. The API enforces
 * `admin:users` on every admin route and is what actually protects the data.
 */
export function AdminGuard({ children }: AdminGuardProps) {
  const user = useAtomValue(userAtom);
  const navigate = useNavigate();
  const allowed = isAdmin(user);

  useEffect(() => {
    if (user && !allowed) {
      navigate({ to: '/', replace: true });
    }
  }, [user, allowed, navigate]);

  // Render nothing while the profile loads, and while the redirect above is in
  // flight, so admin chrome never flashes at someone who may not see it.
  if (!user || !allowed) {
    return null;
  }

  return <>{children}</>;
}
